import React, { useContext, useEffect } from "react"
import { Button } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCommentAltDots, faCommentAltLines, IconDefinition } from "@fortawesome/pro-solid-svg-icons"
import { useTranslation } from "react-i18next"
import styles from "./ArticleToolbar.module.scss"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { selectArticleMarkedMessagesCount, selectCurrentArticle } from "../../store/knowledgeBase/selectors"
import { RESULTS_PER_PAGE } from "../../utility/knowledgeBase/sampleQuestions"
import { setOperatorQuestionsCount } from "../../store/knowledgeBase/thunks"
import { testId } from "../../utility/tests/testId/knowledgeBase"
import KnowledgeBaseContext from "../KnowledgeBaseProvider/KnowledgeBaseContext"
import { QuestionAndAnswersComparisonResult } from "../../models/articleCompare"
import cn from "classnames"

const tNamespace = "knowledgeBase"

interface ArticleToolbarProps {
    isInWorkplace?: boolean
    answersCount?: number
    questionsCount?: number
    onOpenAnswers?: () => void
    onOpenQuestions?: () => void
    questionComparison?: QuestionAndAnswersComparisonResult
}

interface ToolbarItemProps {
    icon: IconDefinition
    itemsCount: number
    titleTranslation: string
    onClick?: () => void
    comparison?: QuestionAndAnswersComparisonResult
}

const ToolbarItem: React.FC<ToolbarItemProps> = props => {
    const { icon, itemsCount, titleTranslation, onClick, comparison } = props
    const { t } = useTranslation()

    const isEqual = comparison ? comparison.CountAfterCompare === comparison.OldCount : false
    const isNothingToCompare = comparison ? comparison.isNothingToCompare : true

    return (
        <Button
            variant="link"
            className={cn(styles.articleToolbar__item, isEqual && !isNothingToCompare && styles.articleToolbar_removed)}
            onClick={onClick}
        >
            <FontAwesomeIcon icon={icon} size="1x" className={styles.articleToolbar__icon} />
            {`${t(`${tNamespace}:${titleTranslation}`)}`}
            {comparison && !isNothingToCompare && !isEqual ? (
                <>
                    <span className={styles.articleToolbar__number}>{`(${comparison.OldCount})`}</span>
                    {comparison.CountAfterCompare}
                </>
            ) : (
                <>{` (${isEqual && comparison ? comparison.CountAfterCompare : itemsCount})`}</>
            )}
        </Button>
    )
}

const ArticleToolbar: React.FC<ArticleToolbarProps> = props => {
    const {
        answersCount = 0,
        questionsCount = 0,
        isInWorkplace,
        questionComparison,
        onOpenAnswers,
        onOpenQuestions
    } = props
    const dispatch = useAppDispatch()
    const currentArticle = useAppSelector(selectCurrentArticle)
    const markedMessagesCount = useAppSelector(selectArticleMarkedMessagesCount)
    const { shareId } = useContext(KnowledgeBaseContext)
    const questionsSum = questionsCount + markedMessagesCount

    const showToolbarItem = (count: number) => {
        return isInWorkplace ? count > 1 : true
    }

    useEffect(() => {
        const symbolCode = currentArticle?.Article?.SymbolCode ?? ""
        !shareId && setOperatorQuestionsCount(symbolCode, RESULTS_PER_PAGE, dispatch)
    }, [shareId])

    return (
        <div className={styles.articleToolbar} data-testid={testId.articleToolbar}>
            {onOpenAnswers && showToolbarItem(answersCount) && (
                <ToolbarItem
                    icon={faCommentAltDots}
                    itemsCount={answersCount}
                    titleTranslation="answer-options"
                    onClick={onOpenAnswers}
                />
            )}
            {onOpenQuestions && showToolbarItem(questionsSum) && (
                <ToolbarItem
                    icon={faCommentAltLines}
                    itemsCount={questionsSum}
                    titleTranslation="question-samples"
                    onClick={onOpenQuestions}
                    comparison={questionComparison}
                />
            )}
        </div>
    )
}

export default ArticleToolbar
